import type { ModuleKey } from '@/access/modules';
import {
  Award,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Banknote,
  Coins,
  Gift,
  GraduationCap,
  Package,
  ListTodo,
  Target,
  Trophy,
  FileText,
  MessageSquareHeart,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  MessageSquare,
  Settings,
  ToggleRight,
  Users,
  type LucideIcon,
} from 'lucide-react';

export type AdminNavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** מפתח הרשאה. null = גלוי לכל מי שנכנס לאזור הניהול. */
  perm: string | null;
  /**
   * המודול שהפריט שייך אליו. null = ליבת הניהול, שאינה נכבית.
   *
   * בלי זה כיבוי מודול הסתיר את מסכי העובד והשאיר את הטאב בניהול
   * דולק — כלומר "כיבוי" שלא כיבה.
   */
  module?: ModuleKey | null;
  end: boolean;
};

export type AdminNavGroup = { title: string | null; items: AdminNavItem[] };

/**
 * ניווט הקונסולה. מקובץ לפי תחום, כי בדסקטופ הכל גלוי בבת אחת
 * ורשימה שטוחה של עשר שורות לא אומרת כלום.
 */
export const ADMIN_NAV: AdminNavGroup[] = [
  {
    title: null,
    // הודעות יושבות מיד אחרי הדשבורד ולא בתוך תפעול: זה המסך
    // שמנהל פותח הכי הרבה פעמים ביום, ובתפעול הוא היה תשיעי
    // ברשימה.
    items: [
      { to: '/admin', label: 'דשבורד', icon: LayoutDashboard, perm: null, end: true },
      {
        to: '/admin/channels',
        module: 'messages',
        label: 'הודעות',
        icon: MessageSquare,
        perm: 'messages.channels',
        end: false,
      },
    ],
  },
  {
    title: 'אנשים',
    items: [
      // בקשות, הרשמה ב QR וייבוא אינם פריטי תפריט משלהם. כולם
      // נוגעים לעובדים, והכניסה אליהם היא כפתור בסרגל של מסך
      // העובדים — שם גם רואים כמה ממתינות.
      { to: '/admin/employees', label: 'עובדים', icon: Users, perm: 'employees.manage', end: true },
    ],
  },
  {
    title: 'תפעול',
    items: [
      {
        to: '/admin/schedule',
        module: 'schedule',
        label: 'סידור עבודה',
        icon: CalendarDays,
        perm: 'schedule.manage',
        end: false,
      },
      // ימים סגורים אינם פריט תפריט משלהם. הם חלק מסידור העבודה,
      // והכניסה אליהם היא כפתור בסרגל של הלוח: עוד שורה בתפריט
      // עולה יותר ממה שמסך שנפתח פעם בחודש מחזיר.
      {
        to: '/admin/ops/checklists',
        module: 'ops_checklists',
        label: 'צ׳קליסטים',
        icon: ClipboardCheck,
        perm: 'ops.manage',
        end: true,
      },
      {
        to: '/admin/ops/tasks',
        module: 'ops_tasks',
        label: 'משימות',
        icon: ListTodo,
        perm: 'ops.tasks',
        end: true,
      },
      // ויטרינה אינה פריט תפריט משלה. היא ההוכחה שהפלנוגרמה בוצעה,
      // והכניסה אליה היא כפתור במסך הפלנוגרמה עצמו.
      {
        // בתפעול ולא ליד "מוצרים" שבלמידה: מי שמסדר מדף מחפש
        // אותה עם הצ'קליסטים והוויטרינה, לא עם הקטלוג
        to: '/admin/products/planogram',
        module: 'learning_planogram',
        label: 'פלנוגרמה',
        icon: LayoutGrid,
        perm: 'products.manage',
        end: true,
      },
      {
        to: '/admin/documents',
        module: 'ops_documents',
        label: 'נהלים',
        icon: FileText,
        perm: 'docs.manage',
        end: true,
      },
      {
        to: '/admin/surveys',
        module: 'ops_surveys',
        label: 'סקרים ומשוב',
        icon: MessageSquareHeart,
        perm: 'surveys.manage',
        end: true,
      },
    ],
  },
  {
    title: 'למידה',
    items: [
      {
        to: '/admin/learning',
        module: 'learning_courses',
        label: 'לומדות',
        icon: GraduationCap,
        perm: 'learning.manage',
        end: true,
      },
      {
        to: '/admin/learning/assignments',
        module: 'learning_courses',
        label: 'שיוך לומדות',
        icon: ClipboardList,
        perm: 'learning.manage',
        end: true,
      },
      // מחלקות ו"הכר את המוצר" אינם פריטי תפריט משלהם. שניהם
      // עובדים על אותו קטלוג, והכניסה אליהם היא כפתור בסרגל של
      // מסך המוצרים — כמו בקשות אצל עובדים.
      {
        to: '/admin/products',
        module: 'learning_products',
        label: 'מוצרים ומחלקות',
        icon: Package,
        perm: 'products.manage',
        end: true,
      },
    ],
  },
  {
    title: 'משחקונים',
    items: [
      {
        to: '/admin/games/points',
        module: 'games',
        label: 'מתן נקודות',
        icon: Coins,
        perm: 'points.grant',
        end: true,
      },
      {
        to: '/admin/games/rewards',
        module: 'games',
        label: 'פרסים',
        icon: Gift,
        perm: 'games.manage',
        end: true,
      },
      {
        to: '/admin/games/competitions',
        module: 'games',
        label: 'תחרויות',
        icon: Trophy,
        perm: 'games.manage',
        end: true,
      },
      {
        to: '/admin/games/awards',
        module: 'games',
        label: 'מצטיינים',
        icon: Award,
        perm: 'games.manage',
        end: true,
      },
    ],
  },
  {
    title: 'מכירות',
    items: [
      {
        to: '/admin/sales',
        module: 'sales',
        label: 'מכירות',
        icon: Banknote,
        perm: 'sales.view',
        end: true,
      },
      {
        to: '/admin/sales/targets',
        module: 'sales',
        label: 'יעדים',
        icon: Target,
        perm: 'sales.targets',
        end: true,
      },
    ],
  },
  {
    title: 'מערכת',
    items: [
      {
        to: '/admin/settings',
        label: 'הגדרות',
        icon: Settings,
        perm: 'admin.settings',
        end: false,
      },
      {
        to: '/admin/roles',
        label: 'תפקידים והרשאות',
        icon: KeyRound,
        perm: 'admin.roles',
        end: false,
      },
      {
        to: '/admin/platform',
        label: 'פלטפורמה',
        icon: ToggleRight,
        perm: 'platform.manage',
        end: true,
      },
    ],
  },
];

/** מסנן לפי ההרשאות של העובד, ומשמיט קבוצה שנשארה ריקה. */
/** מסנן לפי ההרשאות ולפי המודולים הפעילים, ומשמיט קבוצה שנשארה ריקה. */
export function visibleNav(
  permissions: string[] | undefined,
  modules?: Partial<Record<ModuleKey, boolean>>,
): AdminNavGroup[] {
  const has = (perm: string | null) => perm === null || (permissions?.includes(perm) ?? false);
  // בלי modules כלל (ההגדרות עוד לא נטענו) לא מסתירים כלום, אחרת
  // התפריט היה קופץ בטעינה
  const on = (key: ModuleKey | null | undefined) =>
    key == null || modules === undefined || modules[key] === true;

  return ADMIN_NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => has(item.perm) && on(item.module)),
  })).filter((group) => group.items.length > 0);
}
